import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Loader2, LogOut, Search, Users } from 'lucide-react';
import { ApiService } from '../../services/api';
import Turnstile from '../../components/Turnstile';
import Modal from '../../components/ui/Modal';
import WebStudentReportCard from '../practice-exams/components/WebStudentReportCard';
import { useAuth } from '../../contexts/AuthContext';
import type { ExternalResultSummary, PublicSchoolInfo, SchoolAdminExam } from '../../types/external-school';
import { KarneShell, RankCards, RankNote, buildReportData, formatDate, formatNet, rankText, saveBlob } from './shared';

// Okul yöneticisi oturumu (8 saat) sekme kapanınca biter.
const storageKey = (slug: string) => `karne-admin:${slug}`;
const readToken = (slug: string) => { try { return sessionStorage.getItem(storageKey(slug)); } catch { return null; } };
const writeToken = (slug: string, token: string | null) => {
    try { if (token) sessionStorage.setItem(storageKey(slug), token); else sessionStorage.removeItem(storageKey(slug)); } catch { /* özel pencere */ }
};

/**
 * Okul yöneticisi: okulun bağlantısından şifreyle girer; okulunun öğrencilerinin deneme sonuçlarını (T.C. maskeli) ve
 * karnelerini görür, toplu PDF indirir.
 */
export default function PublicSchoolAdmin() {
    const { slug = '' } = useParams();
    const { systemConfig } = useAuth();
    const [school, setSchool] = useState<PublicSchoolInfo | null>(null);
    const [linkError, setLinkError] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(() => readToken(slug));

    const [password, setPassword] = useState('');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [turnstileKey, setTurnstileKey] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [exams, setExams] = useState<SchoolAdminExam[]>([]);
    const [examId, setExamId] = useState('');
    const [rows, setRows] = useState<ExternalResultSummary[]>([]);
    const [loadingRows, setLoadingRows] = useState(false);
    const [query, setQuery] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [detail, setDetail] = useState<{ row: ExternalResultSummary; data: any | null } | null>(null);

    useEffect(() => {
        ApiService.getKarneSchool(slug)
            .then((info) => { setSchool(info); document.title = `Okul paneli · ${info.name}`; })
            .catch((err) => setLinkError(err?.message || 'Bağlantı geçersiz ya da süresi dolmuş'));
    }, [slug]);

    const logout = useCallback((message?: string) => {
        writeToken(slug, null);
        setToken(null);
        setExams([]);
        setRows([]);
        setExamId('');
        setTurnstileKey((k) => k + 1);
        if (message) setError(message);
    }, [slug]);

    const handleError = useCallback((err: any, fallback: string) => {
        if (err?.status === 401) logout('Oturum süresi doldu; lütfen yeniden giriş yapın.');
        else setError(err?.message || fallback);
    }, [logout]);

    useEffect(() => {
        if (!token || !school) return;
        ApiService.getKarneAdminExams(slug, token)
            .then((list) => {
                setExams(list);
                if (list.length) setExamId((current) => current || list[0].examId);
            })
            .catch((err) => handleError(err, 'Denemeler alınamadı'));
    }, [token, school, slug, handleError]);

    useEffect(() => {
        if (!token || !examId) return;
        setLoadingRows(true);
        ApiService.getKarneAdminResults(slug, examId, token)
            .then((res) => setRows(res.rows || []))
            .catch((err) => handleError(err, 'Sonuçlar alınamadı'))
            .finally(() => setLoadingRows(false));
    }, [token, examId, slug, handleError]);

    const login = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!turnstileToken) return;
        setBusy(true);
        setError(null);
        try {
            const res = await ApiService.karneAdminLogin(slug, { password, turnstileToken });
            writeToken(slug, res.token);
            setToken(res.token);
            setPassword('');
        } catch (err: any) {
            setError(err?.message || 'Giriş yapılamadı');
            setTurnstileKey((k) => k + 1);
        } finally {
            setBusy(false);
        }
    };

    const openDetail = async (row: ExternalResultSummary) => {
        if (!token) return;
        setDetail({ row, data: null });
        try {
            const data = buildReportData(await ApiService.getKarneAdminResult(slug, row.resultId, token));
            setDetail({ row, data });
        } catch (err: any) {
            setDetail(null);
            handleError(err, 'Karne açılamadı');
        }
    };

    // Sunucu tek istekte en çok 50 karne basar (26 Eki: büyük toplu basım sunucu belleğini tüketiyordu); kalabalık okul parça parça iner
    const downloadAll = async () => {
        if (!token || !examId) return;
        setDownloading(true);
        try {
            const ids = rows.map((row) => row.resultId);
            if (ids.length <= 50) {
                saveBlob(await ApiService.downloadKarneAdminReportCards(slug, examId, token), 'karneler.pdf');
            } else {
                for (let i = 0; i < ids.length; i += 50) {
                    const part = ids.slice(i, i + 50);
                    const file = await ApiService.downloadKarneAdminReportCards(slug, examId, token, part);
                    const base = (file.fileName || 'karneler.pdf').replace(/\.pdf$/i, '');
                    saveBlob({ blob: file.blob, fileName: `${base}_${String(i + 1).padStart(3, '0')}-${String(i + part.length).padStart(3, '0')}.pdf` }, 'karneler.pdf');
                }
            }
        } catch (err: any) {
            handleError(err, 'PDF indirilemedi');
        } finally {
            setDownloading(false);
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLocaleLowerCase('tr-TR');
        if (!q) return rows;
        return rows.filter((row) => [row.fullName, row.studentNumber, row.className].some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(q)));
    }, [rows, query]);
    const selectedExam = exams.find((exam) => exam.examId === examId) || null;

    if (linkError) {
        return (
            <KarneShell>
                <div className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center">
                    <h1 className="text-lg font-semibold text-slate-900">Okul paneli açılamadı</h1>
                    <p className="mt-1 text-sm text-slate-600">{linkError}.</p>
                </div>
            </KarneShell>
        );
    }
    if (!school) return <KarneShell><div className="py-20 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></div></KarneShell>;

    if (!token) {
        return (
            <KarneShell schoolName={school.name}>
                <div className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h1 className="text-xl font-semibold text-slate-900">Okul paneli</h1>
                    <p className="mt-1 text-sm text-slate-600">{school.name} öğrencilerinin deneme sonuçları. Size verilen yönetici şifresiyle girin.</p>
                    {!school.hasAdminPassword ? (
                        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">Bu okul için henüz yönetici şifresi tanımlanmadı.</p>
                    ) : (
                        <form onSubmit={login} className="mt-5 space-y-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">Yönetici şifresi</label>
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-base focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <Turnstile onToken={setTurnstileToken} resetKey={turnstileKey} />
                            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                            <button
                                type="submit"
                                disabled={busy || !password || !turnstileToken}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                                {busy && <Loader2 size={16} className="animate-spin" />}
                                Giriş
                            </button>
                        </form>
                    )}
                </div>
            </KarneShell>
        );
    }

    return (
        <KarneShell schoolName={school.name}>
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-xl font-semibold text-slate-900">Okul paneli</h1>
                    <button onClick={() => logout()} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                        <LogOut size={15} /> Çıkış
                    </button>
                </div>
                {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

                {exams.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-500">Henüz yüklenmiş deneme sonucu yok.</div>
                ) : (
                    <>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                            <label className="flex-1">
                                <span className="mb-1 block text-sm font-medium text-slate-700">Deneme</span>
                                <select value={examId} onChange={(e) => setExamId(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                                    {exams.map((exam) => (
                                        <option key={exam.examId} value={exam.examId}>
                                            {exam.examName} · {formatDate(exam.examDate)}{exam.gradeLevel ? ` · ${exam.gradeLevel}. sınıf` : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                onClick={downloadAll}
                                disabled={downloading || !rows.length}
                                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                                {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Tüm karneler (PDF)
                            </button>
                        </div>

                        {selectedExam && (
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                <div className="rounded-xl border border-slate-200 bg-white p-4">
                                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Öğrenci</div>
                                    <div className="mt-1 flex items-center gap-2 text-2xl font-bold text-slate-900"><Users size={20} className="text-slate-400" />{selectedExam.studentCount}</div>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-white p-4">
                                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Okul ortalaması</div>
                                    <div className="mt-1 text-2xl font-bold text-slate-900">{formatNet(selectedExam.averageNet)} net</div>
                                </div>
                            </div>
                        )}

                        <div className="rounded-xl border border-slate-200 bg-white">
                            <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
                                <Search size={16} className="text-slate-400" />
                                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ad, okul no ya da sınıf ara" className="w-full border-0 bg-transparent text-sm focus:outline-none focus:ring-0" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-xs text-slate-600">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Öğrenci</th>
                                            <th className="px-3 py-2 text-left">Sınıf</th>
                                            <th className="px-3 py-2 text-left">Okul no</th>
                                            <th className="px-3 py-2 text-left">T.C.</th>
                                            <th className="px-3 py-2 text-right">Net</th>
                                            <th className="px-3 py-2 text-right">Okul sırası</th>
                                            <th className="px-3 py-2 text-right">Genel sıra</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingRows ? (
                                            <tr><td colSpan={7} className="py-10 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></td></tr>
                                        ) : filtered.map((row) => (
                                            <tr key={row.resultId} onClick={() => openDetail(row)} className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/50">
                                                <td className="px-3 py-2 font-medium text-slate-900">{row.fullName || '-'}</td>
                                                <td className="px-3 py-2 text-slate-600">{row.className || '-'}</td>
                                                <td className="px-3 py-2 text-slate-600">{row.studentNumber || <span className="text-red-600">yok</span>}</td>
                                                <td className="px-3 py-2 text-slate-500">{row.tc || <span className="text-red-600">yok</span>}</td>
                                                <td className="px-3 py-2 text-right font-semibold text-slate-900">{formatNet(row.totalNet)}</td>
                                                <td className="px-3 py-2 text-right text-slate-600">{rankText(row.ranks?.school)}</td>
                                                <td className="px-3 py-2 text-right text-slate-600">{rankText(row.ranks?.general)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <RankNote />
                    </>
                )}
            </div>

            <Modal isOpen={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.row.fullName || 'Karne'} size="xl">
                {detail && (
                    <div className="space-y-4">
                        <RankCards summary={detail.row} />
                        {!detail.data ? (
                            <div className="py-16 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></div>
                        ) : (
                            <WebStudentReportCard
                                data={detail.data}
                                examName={selectedExam?.examName || ''}
                                companyLogo={systemConfig?.companyLogo || undefined}
                                companyName={systemConfig?.companyName || undefined}
                                totalStudents={detail.row.ranks?.general?.participantCount || 0}
                                disableAi
                            />
                        )}
                    </div>
                )}
            </Modal>
        </KarneShell>
    );
}
